import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UtensilsCrossed, Receipt, User, ShoppingBag } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export const scrollToMenuSection = (navigate) => {
  if (window.location.pathname !== "/") {
    navigate("/");
    setTimeout(() => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" }), 300);
  } else {
    document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
  }
};

function Tab({ icon: Icon, label, active, onClick, testId, badge }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-current={active ? "page" : undefined}
      className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] font-bold transition-colors ${active ? "text-biga-orange" : "text-biga-ink/70"}`}>
      <span className="relative">
        <Icon size={21} strokeWidth={active ? 2.5 : 2} />
        {badge > 0 && <span data-testid="tabbar-cart-count" className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-biga-green text-biga-cream text-[10px] font-black flex items-center justify-center border-2 border-white">{badge}</span>}
      </span>
      {label}
      {active && <span className="absolute top-0 inset-x-6 h-[3px] rounded-b-full bg-biga-orange" />}
    </button>
  );
}

// Phone tab bar: Menu · My Orders · Account · Cart. Guests tapping My Orders/Account get the sign-in box.
export default function MobileCartBar({ user, onAuthClick }) {
  const { count, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cartLive = count > 0 && !open;
  useEffect(() => {
    document.body.classList.add("has-tabbar");
    return () => document.body.classList.remove("has-tabbar");
  }, []);
  useEffect(() => { document.body.classList.toggle("has-cart-pill", count > 0); }, [count]);
  const member = (to) => () => (user ? navigate(to) : onAuthClick());
  return (
    <>
      {!open && (
        <nav data-testid="mobile-tab-bar" aria-label="Main"
          className="fixed bottom-0 left-0 right-0 z-30 sm:hidden bg-white border-t-2 border-biga-ink/10 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] flex px-1"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <Tab icon={UtensilsCrossed} label="Menu" active={pathname === "/"} onClick={() => scrollToMenuSection(navigate)} testId="tabbar-menu" />
          <Tab icon={Receipt} label="My Orders" active={pathname.startsWith("/orders")} onClick={member("/orders")} testId="tabbar-orders" />
          <Tab icon={User} label="Account" active={pathname.startsWith("/account")} onClick={member("/account")} testId="tabbar-account" />
          <Tab icon={ShoppingBag} label="Cart" badge={count} onClick={() => setOpen(true)} testId="tabbar-cart" />
        </nav>
      )}

      {/* Cart pill — floats above the tab bar when there are items */}
      <AnimatePresence>
        {cartLive && (
          <motion.button
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            onClick={() => setOpen(true)}
            data-testid="mobile-cart-bar"
            aria-label={`View cart, ${count} items, ${inr(subtotal)}`}
            className="fixed bottom-[72px] left-3 right-3 z-40 sm:hidden bg-biga-ink text-biga-cream rounded-full flex items-center justify-between px-5 py-3 shadow-[0_18px_40px_-10px_rgba(0,0,0,0.5)]"
          >
            <span className="flex items-center gap-2 font-bold text-sm">
              <span className="w-6 h-6 rounded-full bg-biga-orange flex items-center justify-center text-xs">{count}</span>
              View cart
            </span>
            <span className="font-display font-black text-sm">{inr(subtotal)} {"\u2192"}</span>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
